import type { ReactNode } from 'react';

/**
 * Juego de iconos propio.
 *
 * POR QUE EXISTE ESTE FICHERO
 *
 * La navegacion usaba caracteres de texto —`⌂ ▤ ⋔ ↗ ⋯`— para las cinco areas.
 * Un glifo no es un icono: lo dibuja la fuente que tenga el sistema, asi que
 * el mismo simbolo sale con un grosor en Segoe UI Variable (Windows) y con
 * otro en Roboto (Android), y algunos ni siquiera existen en las dos —`⋔`
 * cae a la caja de reemplazo en varias pilas—. Ademas ninguno de ellos
 * significaba nada: `⋔` para «Entrenar» hay que aprenderselo.
 *
 * Son SVG en linea y no una dependencia (§21): son once trazos, pesan menos
 * que el `import` que haria falta para traerlos, y heredan `currentColor`, que
 * es lo que permite que el estado activo de la navegacion cambie icono y
 * etiqueta a la vez sin duplicar la regla.
 *
 * Todos comparten rejilla de 24, trazo de 1,75 y remates redondos: es lo que
 * hace que un conjunto de iconos se lea como un conjunto y no como una
 * coleccion. `aria-hidden` en todos, sin excepcion: un icono de esta
 * aplicacion siempre va acompañado de su texto, y anunciarlo lo diria dos
 * veces.
 */

interface PropsIcono {
  /** Lado del cuadro, en pixeles. 20 en la navegacion, 16 en linea de texto. */
  tam?: number;
  className?: string;
}

function Svg({
  tam = 20,
  className,
  children,
}: PropsIcono & { children: ReactNode }): ReactNode {
  return (
    <svg
      width={tam}
      height={tam}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(className === undefined ? {} : { className })}
    >
      {children}
    </svg>
  );
}

export function IconoInicio(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3.8 10.4 12 4l8.2 6.4V19a1.5 1.5 0 0 1-1.5 1.5H5.3A1.5 1.5 0 0 1 3.8 19z" />
      <path d="M9.4 20.5v-6.2h5.2v6.2" />
    </Svg>
  );
}

export function IconoPlan(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <rect x="3.2" y="5" width="17.6" height="15.5" rx="3" />
      <path d="M3.2 10h17.6M8 3.5V6.5M16 3.5V6.5" />
    </Svg>
  );
}

/** Barra con discos. Es el unico objeto que aparece en las cinco areas y que
    todo el mundo identifica sin leer la etiqueta. */
export function IconoEntrenar(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3.5 9.5v5M20.5 9.5v5M7 6.5v11M17 6.5v11M7 12h10" />
    </Svg>
  );
}

export function IconoProgreso(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4 3.8V19a1.2 1.2 0 0 0 1.2 1.2H20.2" />
      <path d="m7.5 15.2 3.6-4.2 3 2.4 4.6-6" />
    </Svg>
  );
}

/** Controles deslizantes: lo que hay detras de «Mas» son ajustes, y una
    elipsis solo dice «hay algo mas», que es lo que ya dice la etiqueta. */
export function IconoAjustes(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4 8h8.5M17 8h3M4 16h3.5M12 16h8" />
      <circle cx="14.75" cy="8" r="2.25" />
      <circle cx="9.75" cy="16" r="2.25" />
    </Svg>
  );
}

export function IconoPunta(props: PropsIcono): ReactNode {
  return (
    <Svg tam={18} {...props}>
      <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />
    </Svg>
  );
}

export function IconoAtras(props: PropsIcono): ReactNode {
  return (
    <Svg tam={18} {...props}>
      <path d="m14.5 5.5-6.5 6.5 6.5 6.5" />
    </Svg>
  );
}

export function IconoCerrar(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
    </Svg>
  );
}

export function IconoCheck(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="m5 12.5 4.6 4.6L19 7" />
    </Svg>
  );
}

export function IconoAlerta(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 4.2 21.3 20H2.7z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

export function IconoSinConexion(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M3.5 3.5l17 17" />
      <path d="M6.4 10.3a8 8 0 0 1 3.3-2M14.6 8.6a8 8 0 0 1 3 1.7" />
      <path d="M9.2 13.6a4.4 4.4 0 0 1 5.1-.5" />
      <circle cx="12" cy="17.5" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/**
 * Favorita. Es el UNICO icono con dos estados, y por eso lleva parametro en
 * vez de ser dos componentes: «★» y «☆» eran dos caracteres distintos, con
 * anchos distintos, asi que el boton cambiaba de tamaño al marcar la receta.
 */
export function IconoEstrella({ llena = false, ...props }: PropsIcono & { llena?: boolean }): ReactNode {
  return (
    <Svg {...props}>
      <path
        d="M12 4.5l2.35 4.76 5.25.77-3.8 3.7.9 5.23L12 16.5l-4.7 2.46.9-5.23-3.8-3.7 5.25-.77z"
        fill={llena ? 'currentColor' : 'none'}
      />
    </Svg>
  );
}

/** Sustituir un ejercicio por otro: dos flechas que se relevan. */
export function IconoSustituir(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4 8.5h12.5l-3-3M20 15.5H7.5l3 3" />
    </Svg>
  );
}

/** Para el estado «vacio»: no hay nada, no es que algo haya fallado. */
export function IconoVacio(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6.5 12h11" />
    </Svg>
  );
}

/** Para el estado «primera vez»: lo que falta se crea. */
export function IconoAnadir(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 6v12M6 12h12" />
    </Svg>
  );
}

/*
  Los tipos de comida (§22, fallback de la portada de una receta). Un objeto
  reconocible por hueco del día, en la misma rejilla y el mismo trazo que los
  de la navegación: taza, manzana, plato con cubiertos, rebanada y luna.
*/

export function IconoDesayuno(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M4.5 9.5h11v4a5.5 5.5 0 0 1-5.5 5.5 5.5 5.5 0 0 1-5.5-5.5z" />
      <path d="M15.5 11h1.25a2.25 2.25 0 0 1 0 4.5H15.2" />
      <path d="M8.5 3.8v2.7M11.5 3.8v2.7" />
    </Svg>
  );
}

export function IconoMediaManana(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M12 8.2c-1.7-1.3-5.6-1.3-6.6 2.3-1 3.7 1.5 9 4.3 9 .9 0 1.4-.5 2.3-.5s1.4.5 2.3.5c2.8 0 5.3-5.3 4.3-9-1-3.6-4.9-3.6-6.6-2.3z" />
      <path d="M12 8.2c0-1.9.7-3.3 2.2-4.2" />
    </Svg>
  );
}

export function IconoAlmuerzo(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <circle cx="12.5" cy="12" r="5" />
      <path d="M3.2 4v4a1.5 1.5 0 0 0 3 0V4M4.7 9.5V20" />
      <path d="M20.8 20V4c-1.5.8-2.3 3-2.3 6.5h2.3" />
    </Svg>
  );
}

export function IconoMerienda(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M6.3 19.5v-8.2A3.7 3.7 0 0 1 8 4.5h8a3.7 3.7 0 0 1 1.7 6.8v8.2z" />
    </Svg>
  );
}

export function IconoCena(props: PropsIcono): ReactNode {
  return (
    <Svg {...props}>
      <path d="M19.5 14.2A7.5 7.5 0 0 1 9.8 4.5a7.5 7.5 0 1 0 9.7 9.7z" />
    </Svg>
  );
}
